import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { sentimentoService, SentimentoEntry, CreateSentimentoEntryData } from '@/services/sentimentoService';
import { useAuth } from './AuthContext';

interface SentimentoContextType {
  entries: SentimentoEntry[];
  isLoading: boolean;
  todayEntries: SentimentoEntry[];
  createEntry: (data: CreateSentimentoEntryData) => Promise<SentimentoEntry | null>;
  updateEntry: (entryId: string, updates: Partial<CreateSentimentoEntryData>) => Promise<SentimentoEntry | null>;
  deleteEntry: (entryId: string) => Promise<boolean>;
  getEntriesByDate: (date: string) => Promise<SentimentoEntry[]>;
  getEntriesByEmotion: (emotion: string) => Promise<SentimentoEntry[]>;
  refreshEntries: () => Promise<void>;
  isNoteBelongsToSentiment: (noteId: string) => boolean;
}

const SentimentoContext = createContext<SentimentoContextType | undefined>(undefined);

export function SentimentoProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<SentimentoEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, sessionReady, session } = useAuth();

  // Get today's entries
  const todayEntries = entries.filter(entry => {
    const today = new Date().toISOString().split('T')[0];
    return entry.entry_date === today;
  });

  // CRITICAL FIX: Load user entries when user AND sessionReady are available
  // This ensures the auth token is available before making API calls
  useEffect(() => {
    if (user && sessionReady && session?.access_token) {
      loadEntries();
    } else if (user && !sessionReady) {
      console.log('SentimentoContext: Waiting for session validation...');
    } else {
      setEntries([]);
    }
  }, [user, sessionReady, session?.access_token]);

  const loadEntries = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const userEntries = await sentimentoService.getUserEntries(user.id, 50); // Last 50 entries
      setEntries(userEntries);
    } catch (error) {
      console.error('Error refreshing entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createEntry = async (data: CreateSentimentoEntryData): Promise<SentimentoEntry | null> => {
    setIsLoading(true);
    try {
      const newEntry = await sentimentoService.createEntry(data);
      if (newEntry) {
        // Remove any existing entry for the same date and emotion to avoid duplicates
        setEntries(prev => {
          const filtered = prev.filter(entry => 
            !(entry.entry_date === newEntry.entry_date && entry.emotion === newEntry.emotion)
          );
          return [newEntry, ...filtered];
        });
      }
      return newEntry;
    } catch (error) {
      console.error('Error creating entry:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const updateEntry = async (entryId: string, updates: Partial<CreateSentimentoEntryData>): Promise<SentimentoEntry | null> => {
    setIsLoading(true);
    try {
      const updatedEntry = await sentimentoService.updateEntry(entryId, updates);
      if (updatedEntry) {
        setEntries(prev => prev.map(entry => 
          entry.id === entryId ? updatedEntry : entry
        ));
      }
      return updatedEntry;
    } catch (error) {
      console.error('Error updating entry:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEntry = async (entryId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const success = await sentimentoService.deleteEntry(entryId);
      if (success) {
        setEntries(prev => prev.filter(entry => entry.id !== entryId));
      }
      return success;
    } catch (error) {
      console.error('Error deleting entry:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getEntriesByDate = async (date: string): Promise<SentimentoEntry[]> => {
    if (!user) return [];
    return await sentimentoService.getEntriesByDate(user.id, date);
  };

  const getEntriesByEmotion = async (emotion: string): Promise<SentimentoEntry[]> => {
    if (!user) return [];
    return await sentimentoService.getEntriesByEmotion(user.id, emotion);
  };

  const isNoteBelongsToSentiment = (noteId: string): boolean => {
    return entries.some(entry => entry.note?.id === noteId);
  };

  return (
    <SentimentoContext.Provider
      value={{
        entries,
        isLoading,
        todayEntries,
        createEntry,
        updateEntry,
        deleteEntry,
        getEntriesByDate,
        getEntriesByEmotion,
        refreshEntries: loadEntries,
        isNoteBelongsToSentiment,
      }}
    >
      {children}
    </SentimentoContext.Provider>
  );
}

export const useSentimento = () => {
  const context = useContext(SentimentoContext);
  if (context === undefined) {
    throw new Error('useSentimento must be used within a SentimentoProvider');
  }
  return context;
};